import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { App, markedApplications } from "../applications.ts";
import {
  applicationLogsQuery,
  Logline,
  useQueryApplication,
} from "../useQueryLAW.ts";
import {
  LOG_LEVELS,
  LogsPathQuery,
  parseLogsPathQuery,
} from "../logsPathQuery.ts";

// Default interval when nothing is given in the url: the last 15 minutes
const DEFAULT_INTERVAL_MS = 15 * 60 * 1000;

const pad = (n: number) => n.toString().padStart(2, "0");

// Formats as local time so the value matches the user's clock in a
// datetime-local input, and round trips through the url
function toLocalISOString(date: Date) {
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hour = pad(date.getHours());
  const min = pad(date.getMinutes());
  return `${year}-${month}-${day}T${hour}:${min}`;
}

function logsPathQuery(
  from: string,
  to: string,
  traceId: string,
  levels: string[]
) {
  const parts = [`from=${from}`, `to=${to}`];
  if (traceId.trim()) {
    parts.push(`traceId=${encodeURIComponent(traceId.trim())}`);
  }
  // All levels selected is the same as no level filter, and is left out
  if (levels.length > 0 && levels.length < LOG_LEVELS.length) {
    parts.push(...levels.map((level) => `level=${level}`));
  }
  return parts.join(",");
}

export function ApplicationLogs() {
  const { appId, query } = useParams() as { appId: string; query?: string };
  const app = markedApplications.find((a) => a.appId() == appId);
  if (!app) {
    return <div>Not found</div>;
  }
  const pathQuery = query ? parseLogsPathQuery(query) : null;
  if (query && !pathQuery) {
    return <div>Could not parse {query} as from, to, traceId, level.</div>;
  }
  // Remounting when the url changes resets the inputs and fetches the new query
  return (
    <ApplicationLogsView key={query ?? ""} app={app} pathQuery={pathQuery} />
  );
}

export function ApplicationLogsView(props: {
  app: App | undefined;
  pathQuery: LogsPathQuery | null;
}) {
  const { app, pathQuery } = props;
  const navigate = useNavigate();
  const location = useLocation();

  const defaultTo = new Date();
  const defaultFrom = new Date(defaultTo.getTime() - DEFAULT_INTERVAL_MS);

  const [from, setFrom] = useState<string>(
    toLocalISOString(pathQuery?.start ?? defaultFrom)
  );
  const [to, setTo] = useState<string>(
    toLocalISOString(pathQuery?.end ?? defaultTo)
  );
  const [selectedLogLevels, setSelectedLogLevels] = useState<string[]>(
    pathQuery?.levels ?? LOG_LEVELS
  );
  const [traceIdFilter, setTraceIdFilter] = useState<string>(
    pathQuery?.traceId ?? ""
  );

  // Helper to set "to" to now
  const setToNow = () => setTo(toLocalISOString(new Date()));

  const logsQuery = applicationLogsQuery(
    app,
    { startTime: new Date(from), endTime: new Date(to) },
    {
      levels:
        selectedLogLevels.length === LOG_LEVELS.length
          ? undefined
          : selectedLogLevels,
      traceId: traceIdFilter.trim() || undefined,
    }
  );
  const { data, loading, error, fetchData } = useQueryApplication(logsQuery);

  // StrictMode invokes mount effects twice in dev, this keeps it to one query
  const autoLoaded = useRef(false);
  useEffect(() => {
    // A url with a query is fetched without pressing "Hent logger"
    if (pathQuery && !autoLoaded.current) {
      autoLoaded.current = true;
      fetchData();
    }
  }, []);

  const runQuery = () => {
    if (!app) {
      // There is no url carrying a query across all applications
      fetchData();
      return;
    }
    const path = `/apps/${app.appId()}/logs/${logsPathQuery(
      from,
      to,
      traceIdFilter,
      selectedLogLevels
    )}`;
    if (location.pathname === path) {
      fetchData();
    } else {
      navigate(path);
    }
  };

  return (
    <div style={{ position: "relative" }}>
      <h2>Logger for {app ? app.name : "alle apper"}</h2>
      <div className="d-flex align-items-end mb-3">
        <div className="me-3">
          <label className="form-label mb-1" htmlFor="from-date">
            From
          </label>
          <input
            id="from-date"
            type="datetime-local"
            className="form-control"
            value={from}
            max={to}
            onChange={(e) => setFrom(e.target.value)}
          />
        </div>
        <div className="me-3">
          <label className="form-label mb-1" htmlFor="to-date">
            To
          </label>
          <div className="input-group">
            <input
              id="to-date"
              type="datetime-local"
              className="form-control"
              value={to}
              min={from}
              onChange={(e) => setTo(e.target.value)}
            />
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={setToNow}
              tabIndex={-1}
              style={{ minWidth: "60px" }}
            >
              Now
            </button>
          </div>
        </div>
        <div className="me-3">
          <label className="form-label mb-1" htmlFor="trace-id-filter">
            Trace ID
          </label>
          <input
            id="trace-id-filter"
            type="text"
            className="form-control"
            placeholder="Filter by trace id"
            value={traceIdFilter}
            onChange={(e) => setTraceIdFilter(e.target.value)}
          />
        </div>
        <LogLevelFilter
          selectedLevels={selectedLogLevels}
          onChange={setSelectedLogLevels}
        />
      </div>
      {error && (
        <div className="alert alert-danger">
          <strong>Error:</strong> {error.message}
        </div>
      )}
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <button className="btn btn-primary " onClick={runQuery}>
            {loading ? (
              <span className="spinner-border spinner-border-sm"></span>
            ) : (
              <>{data?.length ? "Oppdater" : "Hent logger"}</>
            )}
          </button>
          <span className="badge bg-info text-dark">
            {data?.length || 0} logs
          </span>
        </div>

        <div className="list-group">
          {data?.map((ll) => (
            <LogLineComponent key={ll.id} logLine={ll} />
          ))}
        </div>
      </div>
    </div>
  );
}

export function TraceIdLogs() {
  const { query } = useParams() as { query: string };
  const queryComponents = parsePathQuery(query);
  if (!queryComponents) {
    return <div>Could not parse {query} as traceId, start, end.</div>;
  }
  return (
    <ApplicationLogsView
      key={query}
      app={undefined}
      pathQuery={{
        traceId: queryComponents.traceId,
        start: queryComponents.start,
        end: queryComponents.end,
      }}
    />
  );
}

function IntervalLogsRoute(props: { levels?: string[] }) {
  const { appId, query } = useParams() as { appId: string; query: string };
  const app = markedApplications.find((a) => a.appId() == appId);
  if (!app) {
    return <div>Not found</div>;
  }
  const queryComponents = parsePathIntervalQuery(query);
  if (!queryComponents) {
    return <div>Could not parse {query} as start, end.</div>;
  }
  return (
    <ApplicationLogsView
      key={query}
      app={app}
      pathQuery={{
        start: queryComponents.start,
        end: queryComponents.end,
        levels: props.levels,
      }}
    />
  );
}

export function IntervalLogs() {
  return <IntervalLogsRoute />;
}

export function ErrorIntervalLogs() {
  return <IntervalLogsRoute levels={["ERROR"]} />;
}

export function WarnIntervalLogs() {
  return <IntervalLogsRoute levels={["WARN"]} />;
}

// Log level filter component
function LogLevelFilter({
  selectedLevels,
  onChange,
}: {
  selectedLevels: string[];
  onChange: (levels: string[]) => void;
}) {
  const toggleLevel = (level: string) => {
    if (selectedLevels.includes(level)) {
      onChange(selectedLevels.filter((l) => l !== level));
    } else {
      onChange([...selectedLevels, level]);
    }
  };

  const toggleAll = () => {
    if (selectedLevels.length === LOG_LEVELS.length) {
      onChange([]);
    } else {
      onChange([...LOG_LEVELS]);
    }
  };

  return (
    <div className="" style={{ marginLeft: "auto" }}>
      <div className="d-flex align-items-center">
        <div className="btn-group">
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={toggleAll}
          >
            {selectedLevels.length === LOG_LEVELS.length
              ? "Clear All"
              : "Select All"}
          </button>
          {LOG_LEVELS.map((level) => {
            const isSelected = selectedLevels.includes(level);
            let btnClass = "btn-sm ";

            switch (level) {
              case "ERROR":
                btnClass += isSelected ? "btn-danger" : "btn-outline-danger";
                break;
              case "WARN":
                btnClass += isSelected ? "btn-warning" : "btn-outline-warning";
                break;
              case "INFO":
                btnClass += isSelected ? "btn-info" : "btn-outline-info";
                break;
              case "DEBUG":
                btnClass += isSelected
                  ? "btn-secondary"
                  : "btn-outline-secondary";
                break;
            }

            return (
              <button
                key={level}
                className={`btn ${btnClass}`}
                onClick={() => toggleLevel(level)}
              >
                {level}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function LogLineComponent(props: { logLine: Logline }) {
  const [expanded, setExpanded] = useState(false);
  const line = props.logLine.line;

  const toggleExpand = () => {
    setExpanded(!expanded);
  };

  if (line.startsWith("{") || line.startsWith(" {")) {
    return (
      <FormattedLogLine
        logLine={props.logLine}
        expanded={expanded}
        toggleExpand={toggleExpand}
      />
    );
  } else {
    return (
      <RawLogLine
        logLine={props.logLine}
        expanded={expanded}
        toggleExpand={toggleExpand}
      />
    );
  }
}

function FormattedLogLine(props: {
  logLine: Logline;
  expanded: boolean;
  toggleExpand: () => void;
}) {
  const { line } = props.logLine;
  const { expanded, toggleExpand } = props;

  try {
    const parsed = cleanJson(line);
    const levelClass = getLevelClass(parsed.level);

    return (
      <div
        className={`list-group-item list-group-item-action ${levelClass}`}
        onClick={toggleExpand}
      >
        <div className="d-flex justify-content-between align-items-center">
          <div className="text-truncate">
            <span className="badge bg-secondary me-2">
              {new Date(parsed.timestamp).toLocaleString("nb-NO", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: false,
              })}
            </span>
            <span>{parsed.message}</span>
          </div>
          <span className="badge">{expanded ? "▲" : "▼"}</span>
        </div>

        {expanded && (
          <div
            className="mt-2 border-top border-secondary pt-2 text-light"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="row mt-1">
              <div className="col-md-2 fw-bold">Trace ID:</div>
              <div className="col-md-10 text-break">
                {parsed.mdc.traceId || "N/A"}
              </div>
            </div>
            <div className="row mt-1">
              <div className="col-md-2 fw-bold">Level:</div>
              <div className="col-md-10">{parsed.level}</div>
            </div>
            <div className="row mt-1">
              <div className="col-md-2 fw-bold">Logger:</div>
              <div className="col-md-10 text-break">{parsed.loggerName}</div>
            </div>
            {/* Add the complete raw log */}
            <div className="mt-3">
              <div className="fw-bold">Log message:</div>
              <div className="mt-2 p-2 bg-dark rounded">
                <pre
                  className="mb-0 small text-break text-light"
                  style={{ whiteSpace: "pre-wrap", wordWrap: "break-word" }}
                >
                  {parsed.message}
                </pre>
              </div>
            </div>
            {parsed.exception && (
              <div className="mt-2">
                <div className="fw-bold text-danger">Exception:</div>
                <div className="mt-2 p-2 bg-dark rounded">
                  <pre
                    className="mb-0 small text-break text-danger"
                    style={{ whiteSpace: "pre-wrap", wordWrap: "break-word" }}
                  >
                    {parsed.exception.exceptionType}: {parsed.exception.message}
                    {"\n"}
                    {parsed.exception.frames
                      .filter((f) => !!f.line)
                      .map(
                        (f) =>
                          `\tat ${f.class}.${f.method}(${f.class
                            .split(".")
                            .pop()}.java:${f.line})`
                      )
                      .join("\n")}
                  </pre>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  } catch (e) {
    console.log(props.logLine);
    console.log(e);
    return (
      <RawLogLine
        logLine={props.logLine}
        expanded={expanded}
        toggleExpand={toggleExpand}
      />
    );
  }
}

function RawLogLine(props: {
  logLine: Logline;
  expanded: boolean;
  toggleExpand: () => void;
}) {
  const { app, line } = props.logLine;
  const { expanded, toggleExpand } = props;

  return (
    <div
      className="list-group-item list-group-item-action"
      onClick={toggleExpand}
    >
      <div className="d-flex justify-content-between align-items-center">
        <div className="text-truncate">
          <span className="badge bg-secondary me-2">{app}</span>
          <span>
            {line.substring(0, 100)}
            {line.length > 100 ? "..." : ""}
          </span>
        </div>
        <span className="badge text-dark">{expanded ? "▲" : "▼"}</span>
      </div>

      {expanded && (
        <div className="mt-2 border-top pt-2 bg-dark text-light">
          <pre
            className="p-2 mt-2 mb-0 small text-break"
            style={{ whiteSpace: "pre-wrap", wordWrap: "break-word" }}
          >
            {line}
          </pre>
        </div>
      )}
    </div>
  );
}

// Helper function to get Bootstrap classes based on log level
function getLevelClass(level: string): string {
  const levelUpper = level.toUpperCase();
  switch (levelUpper) {
    case "ERROR":
      return "list-group-item-danger";
    case "WARN":
      return "list-group-item-warning";
    case "INFO":
      return "list-group-item-info";
    case "DEBUG":
      return "list-group-item-light";
    default:
      return "";
  }
}

interface ParsedLogLine {
  timestamp: string;
  mdc: { traceId?: string };
  level: string;
  loggerName: string;
  message: string;
  exception?: ParsedExceptionLine;
}
interface ParsedExceptionLine {
  exceptionType: string;
  message: string;
  frames: ExceptionFrame[];
}

interface ExceptionFrame {
  class: string;
  method: string;
  line?: number;
}
const cleanJson = (dirtyJson: string): ParsedLogLine => {
  // This preserves:
  // \t (tab, \u0009)
  // \n (newline, \u000A)
  // \r (carriage return, \u000D)

  const cleaned = dirtyJson.replace(
    /[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g,
    ""
  );
  return JSON.parse(cleaned) as ParsedLogLine;
};

function parseDate(dateString: string): Date | null {
  return new Date(dateString);
}

function parsePathQuery(
  query: string
): { traceId: string; start: Date; end: Date } | null {
  const [traceId, startString, endString] = query.split(",");
  const start = parseDate(startString);
  const end = parseDate(endString);
  if (traceId && start && end) {
    return {
      traceId,
      start: start,
      end: end,
    };
  } else {
    return null;
  }
}

function parsePathIntervalQuery(
  query: string
): { start: Date; end: Date } | null {
  const [startString, endString] = query.split(",");
  const start = parseDate(startString);
  const end = parseDate(endString);
  if (start && end) {
    return {
      start: start,
      end: end,
    };
  } else {
    return null;
  }
}
